import React from 'react'
import type { OpportunityItem } from '../types'
import { listOportunidadesFechadas } from '../services/oportunidadesService'
import { getAllowedSymbolsForCurrentUser } from '@/modules/plans/services/planAssetPolicyService'
import { supabase } from '@/lib/supabase'

const CACHE_TTL_MS = 60 * 1000

export function useOportunidadesFechadas(limit = 120) {
  const [items, setItems] = React.useState<OpportunityItem[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const lastLoadedRef = React.useRef<number | null>(null)
  const inflightRef = React.useRef<Promise<void> | null>(null)

  const load = React.useCallback(
    async (options?: { force?: boolean }) => {
      const force = options?.force ?? false
      const now = Date.now()

      if (!force && lastLoadedRef.current && now - lastLoadedRef.current < CACHE_TTL_MS) {
        setLoading(false)
        return
      }

      if (!force && inflightRef.current) {
        try {
          await inflightRef.current
        } catch {
          // erro já refletido em error
        }
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      inflightRef.current = (async () => {
        try {
          const rows = await listOportunidadesFechadas(limit)
          let filtered: OpportunityItem[] = rows
          try {
            const allowed = await getAllowedSymbolsForCurrentUser()
            if (allowed && allowed.length > 0) {
              const set = new Set(
                allowed.map((s) => String(s || '').trim().toUpperCase()),
              )
              filtered = rows.filter((it) =>
                set.has(String(it.symbol || '').trim().toUpperCase()),
              )
            }
          } catch {
            // Em falha na resolução das moedas, mantemos a lista original (fail-open)
          }
          setItems(filtered)
        } catch (e: any) {
          setError(e?.message || 'Falha ao carregar oportunidades encerradas')
        } finally {
          lastLoadedRef.current = Date.now()
          inflightRef.current = null
          setLoading(false)
        }
      })()

      try {
        await inflightRef.current
      } catch {
        // erro já aplicado em setError
      }
    },
    [limit],
  )

  React.useEffect(() => {
    load()
  }, [load])

  // Auto-refresh suave a cada 1 minuto
  React.useEffect(() => {
    const id = setInterval(() => {
      load()
    }, CACHE_TTL_MS)
    return () => clearInterval(id)
  }, [load])

  // Realtime: reflete fechamentos por Sinais rapidamente
  React.useEffect(() => {
    let timer: any = null
    const scheduleReload = (force: boolean) => {
      try {
        if (timer) clearTimeout(timer)
      } catch {}
      timer = setTimeout(() => {
        load({ force })
      }, 600)
    }

    const channel = supabase
      .channel('closed_opps_realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'trading_opportunities' },
        (payload: any) => {
          try {
            const row = payload?.new
            if (!row) return
            if (row.status === 'closed') {
              // Força bypass do cache para refletir imediatamente
              scheduleReload(true)
            }
          } catch {
            // ignore
          }
        },
      )
      .subscribe()

    const onFocus = () => scheduleReload(true)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') scheduleReload(true)
    }
    const onManualRefresh = () => scheduleReload(true)

    try {
      window.addEventListener('focus', onFocus)
    } catch {}
    try {
      document.addEventListener('visibilitychange', onVisibility)
    } catch {}
    try {
      window.addEventListener('opportunities:refresh', onManualRefresh as any)
    } catch {}

    return () => {
      try {
        supabase.removeChannel(channel)
      } catch {}
      try {
        if (timer) clearTimeout(timer)
      } catch {}
      try {
        window.removeEventListener('focus', onFocus)
      } catch {}
      try {
        document.removeEventListener('visibilitychange', onVisibility)
      } catch {}
      try {
        window.removeEventListener('opportunities:refresh', onManualRefresh as any)
      } catch {}
    }
  }, [load])

  return { items, loading, error, reload: load }
}

